/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { register } from '../core/_requests'
import { Link } from 'react-router-dom'
import { PasswordMeterComponent } from '../../../../_metronic/assets/ts/components'
import { useAuth } from '../core/Auth'
import { toast } from 'react-toastify';
import { LanguageHelp, getConfig } from '../../../../_metronic/helpers/LanguageHelper'

const initialValues = {
  NameSurname: '',
  CompanyName: '',
  Email: '',
  Password: '',
  ConfirmPassword: '',
  AcceptTerms: false,
}

const registrationSchema = Yup.object().shape({
  NameSurname: Yup.string()
    .min(3, LanguageHelp('Minimum 3 symbols'))
    .max(50, LanguageHelp('Maximum 50 symbols'))
    .required(LanguageHelp('Name Surname is required')),
  Email: Yup.string()
    .email(LanguageHelp('Wrong email format'))
    .min(3, LanguageHelp('Minimum 3 symbols'))
    .max(100, LanguageHelp('Maximum 100 symbols'))
    .required(LanguageHelp('Email is required')),
  CompanyName: Yup.string()
    .min(3, LanguageHelp('Minimum 3 symbols'))
    .max(50, LanguageHelp('Maximum 50 symbols'))
    .required(LanguageHelp('Company name is required')),
  Password: Yup.string()
    .min(3, LanguageHelp('Minimum 3 symbols'))
    .max(50, LanguageHelp('Maximum 50 symbols'))
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,15})/, LanguageHelp("Your password should be 8-15 characters long and should contain at least one lowercase, one uppercase, one numeric digit and one special characters!"))
    .required(LanguageHelp('Password is required')),
  ConfirmPassword: Yup.string()
    .required(LanguageHelp('Password confirmation is required'))
    .when('Password', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf([Yup.ref('Password')], LanguageHelp("The passwords you entered do not match.")),
    }),
  AcceptTerms: Yup.bool().required(LanguageHelp('You must accept the terms and conditions')),
})

export function Registration() {
  const lang = getConfig()
  const [loading, setLoading] = useState(false)
  const { saveAuth } = useAuth()
  const [signUpSuccess, setSignUpSuccess] = useState<boolean>(false)
  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        const { data: auth } = await register(
          values.NameSurname,
          values.Email,
          values.Password,
          values.ConfirmPassword,
          values.CompanyName,
          lang.selectedLang
        )

        if (auth.httpStatusCode === 400) {
          toast.error(LanguageHelp(auth.message));
          saveAuth(undefined)
          setStatus(LanguageHelp('The registration details is incorrect'))
          setSubmitting(false)
          setLoading(false)
        }
        else { setSignUpSuccess(true) }

      } catch (ex) {
        if (ex instanceof Error) {
          toast.error(LanguageHelp(ex.message));
        } else {
          toast.error(LanguageHelp('An unexpected error has occurred'));
          console.log('Unexpected error', ex);
        }
        saveAuth(undefined)
        setStatus(LanguageHelp('The registration details is incorrect'))
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_signup_form'
      onSubmit={formik.handleSubmit}
    >
      {signUpSuccess === false && (
        <div>
          {/* begin::Heading */}
          <div className='text-center mb-11'>
            <h1 className='text-dark fw-bolder mb-3'>{LanguageHelp("Sign Up")}</h1>
          </div>
          {/* end::Heading */}

          {/* begin::Form group NameSurname */}
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-dark fs-6'>{LanguageHelp("Name Surname")}</label>
            <input
              placeholder={LanguageHelp("Name Surname")}
              type='text'
              autoComplete='off'
              {...formik.getFieldProps('NameSurname')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.NameSurname && formik.errors.NameSurname,
                },
                {
                  'is-valid': formik.touched.NameSurname && !formik.errors.NameSurname,
                }
              )}
            />
            {formik.touched.NameSurname && formik.errors.NameSurname && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.NameSurname}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group CompanyName */}
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-dark fs-6'>{LanguageHelp("Company Name")}</label>
            <input
              placeholder={LanguageHelp("Company Name")}
              type='text'
              autoComplete='off'
              {...formik.getFieldProps('CompanyName')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.CompanyName && formik.errors.CompanyName,
                },
                {
                  'is-valid': formik.touched.CompanyName && !formik.errors.CompanyName,
                }
              )}
            />
            {formik.touched.CompanyName && formik.errors.CompanyName && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.CompanyName}</span>
                </div>
              </div>
            )}
            {/* end::Form group */}
          </div>

          {/* begin::Form group Email */}
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-dark fs-6'>{LanguageHelp("Email")}</label>
            <input
              placeholder={LanguageHelp("Email")}
              type='email'
              autoComplete='off'
              {...formik.getFieldProps('Email')}
              className={clsx(
                'form-control bg-transparent',
                { 'is-invalid': formik.touched.Email && formik.errors.Email },
                {
                  'is-valid': formik.touched.Email && !formik.errors.Email,
                }
              )}
            />
            {formik.touched.Email && formik.errors.Email && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.Email}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group Password */}
          <div className='fv-row mb-8' data-kt-password-meter='true'>
            <div className='mb-1'>
              <label className='form-label fw-bolder text-dark fs-6'>{LanguageHelp("Password")}</label>
              <div className='position-relative mb-3'>
                <input
                  type='password'
                  placeholder={LanguageHelp("Password")}
                  autoComplete='off'
                  {...formik.getFieldProps('Password')}
                  className={clsx(
                    'form-control bg-transparent',
                    {
                      'is-invalid': formik.touched.Password && formik.errors.Password,
                    },
                    {
                      'is-valid': formik.touched.Password && !formik.errors.Password,
                    }
                  )}
                />
                {formik.touched.Password && formik.errors.Password && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.Password}</span>
                    </div>
                  </div>
                )}
              </div>
              {/* begin::Meter */}
              <div
                className='d-flex align-items-center mb-3'
                data-kt-password-meter-control='highlight'
              >
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
              </div>
              {/* end::Meter */}
            </div>
          </div>
          {/* end::Form group */}

          {/* begin::Form group Confirm password */}
          <div className='fv-row mb-5'>
            <label className='form-label fw-bolder text-dark fs-6'>{LanguageHelp("Confirm Password")}</label>
            <input
              type='password'
              placeholder={LanguageHelp("Confirm Password")}
              autoComplete='off'
              {...formik.getFieldProps('ConfirmPassword')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.ConfirmPassword && formik.errors.ConfirmPassword,
                },
                {
                  'is-valid': formik.touched.ConfirmPassword && !formik.errors.ConfirmPassword,
                }
              )}
            />
            {formik.touched.ConfirmPassword && formik.errors.ConfirmPassword && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.ConfirmPassword}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group */}
          <div className='fv-row mb-8'>
            <label className='form-check form-check-inline' htmlFor='kt_login_toc_agree'>
              <input
                className='form-check-input'
                type='checkbox'
                id='kt_login_toc_agree'
                {...formik.getFieldProps('AcceptTerms')}
              />
              <span>
                <a
                  href='#'
                  target='_blank'
                  className='ms-1 link-primary'
                >
                  {LanguageHelp("I accept the terms.")}
                </a>
              </span>
            </label>
            {formik.touched.AcceptTerms && formik.errors.AcceptTerms && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.AcceptTerms}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group */}
          <div className='text-center'>
            <button
              type='submit'
              id='kt_sign_up_submit'
              className='btn btn-lg btn-primary w-100 mb-5'
              disabled={formik.isSubmitting || !formik.isValid || !formik.values.AcceptTerms}
            >
              {!loading && <span className='indicator-label'>{LanguageHelp("Submit")}</span>}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  {LanguageHelp("Please wait...")}{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
            <Link to='/auth/login'>
              <button
                type='button'
                id='kt_login_signup_form_cancel_button'
                className='btn btn-lg btn-light-primary w-100 mb-5'
              >
                {LanguageHelp("Cancel")}
              </button>
            </Link>
            <div className='text-gray-500 text-center fw-semibold fs-6'>
              {LanguageHelp('Are you a Member?')}{' '}
              <Link to='/auth/login' className='link-primary'>
                {LanguageHelp('Login')}
              </Link>
            </div>
          </div>
        </div>
      )}
      {signUpSuccess === true && (
        <div className='mb-10 bg-light-success p-8 rounded'>
          <div className='text'>{LanguageHelp("Sent activate account email. Please check your email.")}</div>
        </div>
      )}

      {/* end::Form group */}
    </form>
  )
}
